<template>
  <div class="member-place-filter">
    <div class="title-row">
      <h3 class="title" v-t="'appMemberPlaceFilter.search'" />
      <a
        class="clear"
        v-t="'appMemberPlaceFilter.action.clear'"
        v-on:click="$_clear"
      />
    </div>
    <input-keyword-place
      v-model="localKeyword"
      v-bind:idPrefix="'member-place'"
      v-bind:placeholder="$t('appMemberPlaceFilter.placeholder.keyword')"
      v-bind:keywordTargets="placeKeywordTargets"
      v-on:keyword-changed="$_onKeywordChanged"
      v-on:keyword-target-changed="$_onKeywordTargetChanged"
    />
    <place-group-selector
      v-bind:onApiError="handleApiError"
      ref="place-group-selector"
      v-on:place-group-change="$_onPlaceGroupChanged"
    />
    <user-selector
      ref="user-selector"
      all-organizations
      v-bind:primary-organization-id="
        $store.getters['userProfile/defaultOrganizationId']
      "
      v-bind:indoor-sites="$store.getters['indoorSite/indoorSites']"
      v-bind:default-text="$t('appMemberPlaceFilter.allUsers')"
      v-bind:keyword-target="userKeywordTargets"
      v-bind:on-api-error="handleApiError"
      v-bind:value="valueForUserSelector"
      v-on:change:value="$_onUserSelected"
      v-on:change:keywordTarget="
        $store.dispatch('userKeywordTarget/setKeywordTargets', $event)
      "
    />
    <datetime-selector
      v-bind:fieldLabel="$t('appMemberPlaceFilter.registrationDate')"
      v-bind:start-date="localCondition.date.start"
      v-bind:end-date="localCondition.date.end"
      v-bind:period-string="null"
      v-bind:period-flg="true"
      v-on:datetime-selectend="$_onDateChanged"
    />
  </div>
</template>

<script>
import InputKeywordPlace from '@/components/AppInputKeywordPlace.vue'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { mapGetters } from 'vuex'
import _ from 'lodash'

/**
 * 空の条件値を取得する
 */
const getBlankCondition = () => ({
  keyword: '',
  placeGroupId: null,
  userIds: [],
  organizationIds: [],
  date: {
    start: null,
    end: null,
  },
})

export default {
  name: 'AppMemberPlaceFilter',
  components: {
    InputKeywordPlace,
  },
  setup() {
    const { handleApiError } = useHandleApiError()
    return { handleApiError }
  },
  data: function () {
    return {
      // ローカル値
      localCondition: getBlankCondition(),
      // 内部用キーワード(リアクティブ化を避けるため分離）
      localKeyword: null,
    }
  },
  computed: {
    ...mapGetters('userKeywordTarget', {
      /**
       * ユーザーのキーワード検索項目
       * @type {Array.<String>}
       */
      userKeywordTargets: 'keywordTargets',
    }),
    ...mapGetters('place', {
      /**
       * ユーザーのキーワード検索項目
       * @type {Array.<String>}
       */
      placeKeywordTargets: 'keywordTargets',
    }),
    /**
     * AppUserSelector 用の値
     * @type {Object[]}
     */
    valueForUserSelector: function () {
      const { userIds, organizationIds } = this.localCondition
      if (userIds.length + organizationIds.length === 0) return null

      return [
        ...organizationIds.map((id) => ({ type: 'organization', id })),
        ...userIds.map((id) => ({ type: 'user', id })),
      ]
    },
    /**
     * 結果のcondition
     */
    resultCondition: function () {
      return {
        ...this.localCondition,
        keywordTargets: this.placeKeywordTargets,
      }
    },
  },
  watch: {
    resultCondition: {
      deep: true,
      handler: function () {
        this.$_emitConditionChangedEvent()
      },
    },
  },
  created: function () {
    this.$_updateIndoorSite()
  },
  methods: {
    /**
     * 屋内サイト情報更新
     */
    $_updateIndoorSite: async function () {
      try {
        await this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch indoorSites. use fetched before.')
        })
      }
    },

    /**
     * フォームのクリア
     */
    $_clear: function () {
      this.localCondition = getBlankCondition()
      this.localKeyword = null
      this.$store.dispatch('place/setKeywordTargets', [])
      this.$refs['place-group-selector'].setValue(-1)
    },

    /**
     * キーワード変更時処理
     * @param {String} keyword キーワード
     */
    $_onKeywordChanged: function (keyword) {
      this.localCondition = {
        ...this.localCondition,
        keyword: keyword,
      }
    },

    /**
     * キーワード検索項目変更時処理
     * @param {Array.<String>} keywordTargets 検索項目
     */
    $_onKeywordTargetChanged: function (keywordTargets) {
      // 一致している場合は何もしない
      if (_.isEqual(this.placeKeywordTargets, keywordTargets)) {
        return
      }
      // 現在のキーワード入力値を検索条件に反映させる
      this.localCondition = {
        ...this.localCondition,
        keyword: this.localKeyword,
      }
      // storeに保存
      this.$store.dispatch('place/setKeywordTargets', keywordTargets)
    },

    /**
     * プレイスグループ変更時処理
     * @param {*} values プレイスグループ
     */
    $_onPlaceGroupChanged: function (values) {
      this.localCondition = {
        ...this.localCondition,
        placeGroupId: values,
        keyword: this.localKeyword,
      }
    },

    /**
     * ユーザー変更時処理
     * @param {Array.<Object>} values 選択肢
     */
    $_onUserSelected: function (values) {
      const { userIds, organizationIds } = (values || []).reduce(
        (result, current) => {
          const { type, id } = current
          if (type === 'organization') {
            result.organizationIds.push(id)
          } else if (type === 'user') {
            result.userIds.push(id)
          }
          return result
        },
        { userIds: [], organizationIds: [] }
      )
      // 一致している場合は何もしない
      if (
        _.isEqual(this.localCondition.userIds, userIds) &&
        _.isEqual(this.localCondition.organizationIds, organizationIds)
      ) {
        return
      }

      this.localCondition = {
        ...this.localCondition,
        userIds,
        organizationIds,
        keyword: this.localKeyword,
      }
    },

    /**
     * 登録日付変更時処理
     * @param {String} startDate 開始日文字列(yyyy-mm-dd)
     * @param {String} startTime 開始時刻文字列(hh:mm)
     * @param {String} endDate 終了日文字列(yyyy-mm-dd)
     * @param {String} endTime 終了時刻文字列(hh:mm)
     * @param {String} errorMessage バリデーションエラーメッセージ
     */
    $_onDateChanged: function (
      startDate,
      startTime,
      endDate,
      endTime,
      errorMessage
    ) {
      if (errorMessage) {
        // バリデーションエラーを表示する
        this.$alert.fire({
          icon: 'error',
          title: this.$t(
            'appMemberPlaceFilter.message.invalidRegistrationDate'
          ),
          text: errorMessage,
        })
        return
      }

      const { start, end } = this.localCondition.date
      // 一致している場合は何もしない
      if (start === startDate && end === endDate) {
        return
      }

      this.localCondition.date = {
        start: startDate,
        end: endDate,
        keyword: this.localKeyword,
      }
    },

    /**
     * 条件変更イベント発行
     */
    $_emitConditionChangedEvent: function () {
      /**
       * 条件変更イベント
       * @param {Object} condition 条件
       */
      this.$emit('condition-changed', this.resultCondition)
    },
  },
}
</script>

<style lang="scss" scoped>
.member-place-filter {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 8px;
}

.title-row {
  width: 100%;
  padding: 0 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1;

  .title {
    font-size: $font-size-middle;
    font-weight: $font-weight-bold;
    margin: 0;
  }

  .clear {
    font-size: $font-size-middle;
    color: $text-color-link;
    text-decoration: $text-color-link underline;

    &:hover {
      cursor: pointer;
    }
  }
}

::v-deep .place-group-selector {
  width: 100%;
}

::v-deep .user-selector {
  width: 100%;
}

::v-deep .datetime-selector {
  width: 100%;
}
</style>
